'use client';

import { captureBrowserEventContext, generateUuidV4 } from '@nextmar/snowplow-sdk';
import type { BrowserEventContext, SignUpUser } from '@nextmar/snowplow-sdk';
import { createZeeloolSnowplowDataAdapters } from '../integrations/snowplow-sdk/adapters';
import { recordSyncedSnowplowUserId } from '../integrations/snowplow-sdk/capturedUserId';
import { captureSensorsEventProperties } from '../providers/sensorProvider';
import { adaptManualReporting } from '../adapters/manualMirrorAdapter';
import type { TrackingEvent } from '../types';
import logger from '@/utils/client-logger';
import type { BrowserAnalyticsConfiguration } from './browserAnalytics';
import {
  configureManualAnalyticsPolicy,
  createManualAnalyticsCoordinator,
  hasManualIdentityControlCharacters,
} from './manualAnalytics';
import type {
  ManualAnalyticsCoordinator,
  ManualOperation,
  SensorsRuntime,
} from './manualAnalyticsTypes';
import { loadAnalyticsScript } from './scriptLoader';
import { syncSensorsIdentityToStorage } from '../utils/sensorsIdentity';
import {
  createAutomaticSensorsMirror,
  type AutomaticSensorsMirror,
} from './automaticSensorsMirror';
import { bindAutomaticSensorsMirrorTarget } from './sensorsMirrorHooks';

/** 页内所有公开手动入口共享同一所有者，配置不创建第二条意图队列。 */
interface ManualBridgeState {
  owner: ManualAnalyticsCoordinator;
  automatic: AutomaticSensorsMirror;
  config?: BrowserAnalyticsConfiguration;
  readiness?: Promise<boolean>;
}
/** 不跨窗口或 SSR 请求共享分析状态。 */
const bridges = new WeakMap<Window, ManualBridgeState>();

/**
 * 自动事件只认最终包的登录证明，恢复旧事件不借当前业务用户或根级客户 ID。
 * @param payload 已通过镜像事实校验的同条最终 Sensors 包；可能属于先前页面或账号。
 * @returns 页面及浏览器快照；登录 UUID 仅由同条 login_id 证明，客户资料和根 uid 留空。
 */
function captureAutomaticSensorsContext(
  payload: Readonly<Record<string, unknown>>,
): BrowserEventContext {
  const loginId =
    typeof payload.login_id === 'string' && payload.login_id === payload.distinct_id
      ? payload.login_id
      : undefined;
  return captureBrowserEventContext({
    /** 无入参；最终发送包已经拥有通过校验的匿名身份，返回 true。 */
    isSensorsInitialized: () => true,
    /** 无入参；返回同条最终包身份，不读取当前 Cookie 或 SDK 的未来身份。 */
    getIdentity() {
      return {
        distinctId: payload.distinct_id as string,
        anonymousId: payload.anonymous_id as string,
      };
    },
    /** 无入参；同条包没有 customer_id 归属证明，返回 null 禁止未来根 uid 回填。 */
    getTrackerUserId: () => null,
    /** 无入参；只返回同条登录 UUID 和登录状态，不携带当前客户资料或 PII。 */
    getUser: () => (loginId ? { uuid: loginId, isLoggedIn: true } : { isLoggedIn: false }),
    /** 无入参；返回空归因，已有最终 pub_* 由 SDK 同条事实映射负责。 */
    getAttribution: () => ({}),
    /** 无入参；只返回同条最终平台语言，旧事件不读取当前语言 Cookie 或缓存。 */
    getPlatformLanguage() {
      const value = (payload.properties as Readonly<Record<string, unknown>>).pub_platform_language;
      return typeof value === 'string' && value.trim() ? value : undefined;
    },
  });
}

/**
 * 返回当前文档唯一手动协调器，首次登记主动唤醒物理加载。
 * @returns 本窗口协调器；SSR 不创建状态并明确拒绝。
 */
export function getManualAnalyticsCoordinator(): ManualAnalyticsCoordinator {
  if (typeof window === 'undefined') throw new Error('manual_browser_missing');
  const existing = bridges.get(window);
  if (existing) return existing.owner;
  const state = {} as ManualBridgeState;
  state.owner = createManualAnalyticsCoordinator({
    now: Date.now,
    createIntentId: generateUuidV4,
    /** 无入参；仅初始化就绪后返回 true，等待不会执行业务 API。 */
    ensureReady: () => {
      state.readiness ??= Promise.resolve()
        .then(async () => {
          const config = state.config;
          if (config && !config.sensors.isEnabled) return false;
          await loadAnalyticsScript('sensors');
          const sensors = window.sensorsDataAnalytic201505 as unknown as SensorsRuntime;
          state.owner.install(sensors);
          if (!config) return false;
          if (!(await config.sensors.init())) return false;
          if (!config.snowplow.isEnabled) return true;
          // 资源已物理共用，保留原逻辑消费者入口及其加载校验。
          await loadAnalyticsScript('classic');
          if (!(await config.snowplow.init())) return false;
          return config.client.getState().status === 'ready';
        })
        .catch(() => false)
        .finally(() => {
          state.readiness = undefined;
        });
      return state.readiness;
    },
    /** @param entry 稳定标量诊断。@returns 无返回值，不保存载荷或身份。 */
    onDiagnostic(entry) {
      const observed = state.config?.onManualDiagnostic?.(Object.freeze({ ...entry }));
      if (observed) void Promise.resolve(observed).catch(() => undefined);
      if (entry.stage === 'integration_failed' && entry.reason !== 'disabled')
        logger.error('[manualAnalytics] integration_failed', entry);
    },
  });
  state.automatic = createAutomaticSensorsMirror({
    captureContext: captureAutomaticSensorsContext,
    /** 无入参；原初始化队列负责物理就绪，自动旁路只查询结果，返回 Promise<boolean>。 */
    async ensureReady() {
      const config = state.config;
      if (!config || !config.sensors.isEnabled || !config.snowplow.isEnabled) return false;
      if (!(await config.sensors.init())) return false;
      await loadAnalyticsScript('classic');
      return config.snowplow.init();
    },
    /** @param reason 稳定失败类别，不携带原事件或身份。@returns 无返回值。 */
    diagnose(reason) {
      logger.warn('[automaticSensorsMirror] integration_failed', { reason });
    },
  });
  bindAutomaticSensorsMirrorTarget(state.owner, state.automatic);
  bridges.set(window, state);
  return state.owner;
}

/** @param config 原初始化入口解析出的配置。@returns 无返回值，不猜测设备或远程开关。 */
export function configureManualAnalytics(config: BrowserAnalyticsConfiguration): void {
  const owner = getManualAnalyticsCoordinator();
  const state = bridges.get(window)!;
  if (state.config) return;
  state.config = config;
  configureManualAnalyticsPolicy(owner, {
    sensorsEnabled: config.sensors.isEnabled,
    mirrorEnabled: config.snowplow.isEnabled,
  });
  owner.connect(config.client);
  state.automatic.configure(config.client, config.sensors.isEnabled && config.snowplow.isEnabled);
  owner.wake();
}

/** @param sensors 当前窗口的真实 SDK。@returns 无返回值，必须先于原 init 调用。 */
export function installManualAnalytics(sensors: SensorsRuntime): void {
  getManualAnalyticsCoordinator().install(sensors);
}

/**
 * 在同一初始化拥有者内执行自动采集，并关联同步产生的先 PV 后首次画像。
 * @param sensors 当前窗口实际 Sensors 实例，必须已经安装原共享 hook。
 * @returns 原 quick(autoTrack) 的结果；作用域不越过当前同步调用栈。
 */
export function runSensorsAutoTrack(sensors: SensorsRuntime): unknown {
  getManualAnalyticsCoordinator();
  return bridges.get(window)!.automatic.runAutoTrack(() => sensors.quick('autoTrack'));
}

/** 无入参；仅冲刷已存在的自动旁路，不为生命周期通知创建新拥有者，返回 void。 */
export function flushAutomaticSensorsMirrors(): void {
  if (typeof window !== 'undefined') bridges.get(window)?.automatic.flushNow();
}

/** 无入参；BFCache 恢复只唤醒未终态镜像，不重放原 Sensors 或创建 PV，返回 void。 */
export function wakeAutomaticSensorsMirrors(): void {
  if (typeof window !== 'undefined') bridges.get(window)?.automatic.wake();
}

/** 无入参；释放本窗口全部等待并移除文档登记，不声明请求已被撤回。 */
export function disposeManualAnalytics(): void {
  if (typeof window === 'undefined') return;
  bridges.get(window)?.owner.dispose();
  bridges.get(window)?.automatic.dispose();
  bridges.delete(window);
}

/** 业务入口可传入已捕获快照，适配器计算不能延后发生归因。 */
export interface ManualOccurrence {
  context?: BrowserEventContext;
  occurredAt?: number;
  sensorsProperties?: Readonly<Record<string, unknown>>;
}

/**
 * 累计进入登记前的接入失败，防止适配或快照异常被 void 入口吞掉。
 * @param source 固定出口名，不读取业务属性。
 * @param reason 稳定错误类别，不携带原始异常。
 * @returns 固定 false；日志异常也不会传播到业务。
 */
export function recordManualRegistrationFailure(source: string, reason: string): false {
  getManualAnalyticsCoordinator().register(null as never);
  try {
    logger.error('[manualAnalytics] registration_failed', { source, reason });
  } catch {
    /* 失败计数已经保留，日志不拥有业务调用。 */
  }
  return false;
}

/**
 * 登记一组保持原调用顺序的动作。
 * @param source 固定代码来源名，不接受 URL 或身份。
 * @param operations 发生时确定的业务动作，SDK 方法只在 FIFO 中执行。
 * @param occurrence 可选已捕获的页面、时间、属性。
 * @returns 仅表示登记结果，不能当成登录完成或入库结果。
 */
export function registerManualOperations(
  source: string,
  operations: readonly ManualOperation[],
  occurrence: ManualOccurrence = {},
): boolean {
  const owner = getManualAnalyticsCoordinator();
  try {
    return owner.register({
      source,
      operations,
      occurredAt: occurrence.occurredAt ?? Date.now(),
      // 业务需求：退出或切换账号后尚未再次登录的行为仍归属退出前账号，快照保留已同步的 trackerUserId。
      // 已登记事件使用发生时的身份快照，后续登录不回写历史事件的身份。
      context:
        occurrence.context ?? captureBrowserEventContext(createZeeloolSnowplowDataAdapters()),
      sensorsProperties: occurrence.sensorsProperties ?? {
        ...captureSensorsEventProperties(),
        $title: document.title,
        $referrer: document.referrer,
      },
    }).accepted;
  } catch {
    return recordManualRegistrationFailure(source, 'capture_failed');
  }
}

/** @param event 原业务事件。@param source 固定公开出口名。@returns 是否登记成功，不直接双发。 */
export function registerManualTrack(event: TrackingEvent, source: string): boolean {
  try {
    const adapted = adaptManualReporting(event, undefined);
    const sensorsProperties: Record<string, unknown> = {
      ...captureSensorsEventProperties(),
      $title: document.title,
      $referrer: document.referrer,
    };
    if (event.options && Object.prototype.hasOwnProperty.call(event.options, 'spmReferSnapshot')) {
      const frozenRefer = Object.prototype.hasOwnProperty.call(event.properties, 'spm')
        ? event.properties.spm
        : event.options.spmReferSnapshot;
      sensorsProperties.spm = frozenRefer ?? null;
    }
    return registerManualOperations(source, adapted.operations, { sensorsProperties });
  } catch {
    return recordManualRegistrationFailure(source, 'adaptation_failed');
  }
}

/**
 * 登记真实登录操作；UUID-only 不构造虚假的 customer ID。
 * @param source 固定登录出口。
 * @param uuid 原 SDK 登录参数。
 * @param customerId 显式客户 ID，仅存在时使用原专用 SignUp 合同。
 * @param afterIdentity 原有缓存和 root uid 同步，在实际 login 后执行。
 * @param user 显式注册输入，保留批准的 email/phone，不读取未来资料。
 * @param occurrence 可选调用发生快照。
 * @returns 登记结果，公开同步 boolean 入口另外回读当前实际身份。
 */
export function registerManualLogin(
  source: string,
  uuid: string,
  customerId?: string,
  afterIdentity?: () => void,
  user?: SignUpUser,
  occurrence?: ManualOccurrence,
): boolean {
  if (typeof uuid !== 'string' || !uuid.trim() || hasManualIdentityControlCharacters(uuid))
    return false;
  const normalized = uuid.trim();
  const mirrorInput = customerId
    ? { kind: 'signUp' as const, user: user ?? { uuid: normalized, customerId } }
    : { kind: 'track' as const, eventName: 'sign_up' };
  return registerManualOperations(
    source,
    [{ kind: 'login', uuid: normalized, mirrorInput, afterIdentity }],
    occurrence,
  );
}

/**
 * 原专用 SignUp 入口转为真实 login 的镜像，原始客户 ID 不借未来资料。
 * @param user 业务显式提供的注册身份及批准用户字段。
 * @param occurrence 发生页面快照。
 * @returns 登记结果，后续相同账号无新 Sensors 事件时不造额外镜像。
 */
export function registerManualSignUp(user: SignUpUser, occurrence?: ManualOccurrence): boolean {
  try {
    const captured = Object.freeze(structuredClone(user));
    return registerManualLogin(
      'z.captureBrowserAnalytics.signUp',
      captured.uuid,
      captured.customerId,
      () => {
        syncSensorsIdentityToStorage();
        const client = bridges.get(window)?.config?.client;
        if (client) {
          client.setUserId(captured.customerId);
          recordSyncedSnowplowUserId(client, captured.customerId);
        }
      },
      captured,
      occurrence,
    );
  } catch {
    return recordManualRegistrationFailure('z.captureBrowserAnalytics.signUp', 'capture_failed');
  }
}
